/**
 * The shared "GHOST LAND" wordmark for the auth + character screens: chunky
 * Silkscreen letters that stagger up/down (first up, second down, third up…)
 * with a tiny animated Pac-Man-style ghost bobbing alongside. Pure black & white
 * to match the UI theme — the ghost's body is a ghostly off-white, its feet
 * wiggle between two frames and its eyes dart around.
 *
 * Usage: `const logo = mountGhostLogo(overlay);` then `logo.dispose()` when the
 * overlay is torn down (stops the animation loop). The element is absolutely
 * positioned at the top-centre of its host and ignores pointer events.
 */
const STYLE_ID = "ghostland-ghostlogo-style";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
.gl-ghost-logo {
  position: absolute; top: 5.5vh; left: 50%; transform: translateX(-50%);
  display: flex; align-items: center; gap: 16px; z-index: 20;
  pointer-events: none; user-select: none; white-space: nowrap;
}
.gl-ghost-logo canvas {
  image-rendering: pixelated; width: 54px; height: 54px;
  animation: gl-ghost-bob 1.8s ease-in-out infinite;
}
@keyframes gl-ghost-bob {
  0%, 100% { transform: translateY(-3px); }
  50%      { transform: translateY(3px); }
}
.gl-ghost-title {
  display: flex; align-items: center;
  font-weight: 700; font-size: 34px; letter-spacing: 0.05em; color: #f4f4f4; line-height: 1;
}
/* win against the theme's \`[id^="gl-"] * { font-family: … !important }\` rule on the
   sans-themed auth overlay — the wordmark must stay pixel Silkscreen everywhere */
.gl-ghost-title, .gl-ghost-title .l { font-family: 'Silkscreen', ui-monospace, monospace !important; }
.gl-ghost-title .l { display: inline-block; text-shadow: 0 3px 0 rgba(0,0,0,.65); }
.gl-ghost-title .up { transform: translateY(-7px); }
.gl-ghost-title .dn { transform: translateY(7px); }
.gl-ghost-title .sp { width: 0.45em; }
`;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

// 14×14 pixel ghost. The dome (rows 0–11) is fixed; the last two rows are the
// wiggling feet, swapped between two frames for the classic shuffle.
const N = 14;
const DOME = [
  "00011111111000",
  "00111111111100",
  "01111111111110",
  "01111111111110",
  "11111111111111",
  "11111111111111",
  "11111111111111",
  "11111111111111",
  "11111111111111",
  "11111111111111",
  "11111111111111",
  "11111111111111",
];
const FEET: ReadonlyArray<ReadonlyArray<string>> = [
  ["11111111111111", "11011011011011"],
  ["11111111111111", "01101101101101"],
];

const BODY = "#e9e9e9"; // ghostly off-white
const SCLERA = "#ffffff";
const PUPIL = "#101015";

export interface GhostLogo {
  el: HTMLElement;
  dispose(): void;
}

/** Build, animate, and append the logo to `host`. Call `dispose()` to stop it. */
export function mountGhostLogo(host: HTMLElement): GhostLogo {
  ensureStyle();

  const el = document.createElement("div");
  el.className = "gl-ghost-logo";

  const canvas = document.createElement("canvas");
  canvas.width = N;
  canvas.height = N;
  const ctx = canvas.getContext("2d")!;

  const title = document.createElement("div");
  title.className = "gl-ghost-title";
  // "GHOST LAND" — alternate up/down per (non-space) letter.
  let li = 0;
  for (const ch of "GHOST LAND") {
    if (ch === " ") {
      const sp = document.createElement("span");
      sp.className = "sp";
      title.appendChild(sp);
      continue;
    }
    const span = document.createElement("span");
    span.className = `l ${li % 2 === 0 ? "up" : "dn"}`;
    span.textContent = ch;
    title.appendChild(span);
    li++;
  }

  el.append(canvas, title);
  host.appendChild(el);

  function drawEye(ex: number, ey: number, lookX: number, lookY: number): void {
    ctx.fillStyle = SCLERA;
    ctx.fillRect(ex, ey, 3, 4);
    ctx.fillStyle = PUPIL;
    const px = ex + (lookX > 0 ? 1 : 0);
    const py = ey + 1 + (lookY > 0 ? 1 : 0);
    ctx.fillRect(px, py, 2, 2);
  }

  function draw(frame: number, lookX: number, lookY: number): void {
    ctx.clearRect(0, 0, N, N);
    ctx.fillStyle = BODY;
    const rows = [...DOME, ...FEET[frame]];
    rows.forEach((row, y) => {
      for (let x = 0; x < row.length; x++) {
        if (row[x] === "1") ctx.fillRect(x, y, 1, 1);
      }
    });
    drawEye(2, 3, lookX, lookY);
    drawEye(9, 3, lookX, lookY);
  }

  let raf = 0;
  let running = true;
  let frame = 0;
  let lastFeet = 0;
  let lookX = 0;
  let lookY = 0;
  let lastLook = 0;
  let nextLook = 900;
  draw(frame, lookX, lookY);

  function loop(t: number): void {
    if (!running) return;
    raf = requestAnimationFrame(loop);
    let dirty = false;
    if (t - lastFeet > 190) {
      frame ^= 1;
      lastFeet = t;
      dirty = true;
    }
    if (t - lastLook > nextLook) {
      lookX = [-1, 0, 0, 1][Math.floor(Math.random() * 4)];
      lookY = [0, 0, 1][Math.floor(Math.random() * 3)];
      lastLook = t;
      nextLook = 700 + Math.random() * 1400;
      dirty = true;
    }
    if (dirty) draw(frame, lookX, lookY);
  }
  raf = requestAnimationFrame(loop);

  return {
    el,
    dispose(): void {
      running = false;
      cancelAnimationFrame(raf);
      el.remove();
    },
  };
}
